import React, { useState } from 'react';
import moment from 'moment';

import WarningIcon from '@mui/icons-material/Warning';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers';

import HardwareList from '../../HardwareList';
import CaseAccordion from '../../CaseAccordion';
import NoAccess from '../../NoAccess';

import Button from '../../../../../shared/Button';
import Dropzone from '../../../../../shared/dropzone/Dropzone';

import { caseConstants } from '../../../../../../constants';

const { matorthoProcedureFlows, procedureFlows } = caseConstants;

export const CmfExtendedStep5 = (props) => {
  const {
    classes,
    onClose,
    activeCase,
    updateCase,
    products,
    designReport,
    designReportRejected,
    manufacturingFiles,
    manufacturingFilesRejected,
    caseMaxilla,
    fileStatuses,
    fileTypes,
    statuses,
    caseNotifications,
    userId,
  } = props;

  const [preop, setPreop] = useState([]);
  const [manufacturing, setManufacturingFiles] = useState([]);
  const [maxillaList, setMaxillaList] = useState(caseMaxilla || []);
  const approvalNote = activeCase.notes?.step4?.trim();
  const showApprovalNote = [
    procedureFlows.CUSTOM_EXTENDED,
    procedureFlows.VILEX,
    ...matorthoProcedureFlows,
  ].includes(activeCase.procedureFlow);

  const requiredDate = activeCase?.date
    ? moment(activeCase.date).subtract(7, 'days')
    : moment();

  const [date, setDate] = useState(requiredDate);

  const handleRemovePreop = (name) => {
    setPreop(preop.filter((file) => file.name !== name));
  };

  const handleRemoveManufacturing = (name) => {
    setManufacturingFiles(manufacturing.filter((file) => file.name !== name));
  };

  /* -------------------- Components -------------------- */

  const handleAddMaxilla = (product) => {
    const list = [...maxillaList];
    const item = list.find(
      (i) => i.product === product.product && i.category === product.category
    );

    if (item) {
      item.amount += Number(product.amount);
      setMaxillaList(list);
    } else {
      setMaxillaList([...maxillaList, product]);
    }
  };

  const handleRemoveMaxilla = (id) => {
    setMaxillaList(maxillaList.filter((i) => i.id !== id));
  };

  const handleMaxillaChange = (id, key, value) => {
    const list = [...maxillaList];
    const item = list.find((i) => i.id === id);
    item[key] = value;
    setMaxillaList(list);
  };

  /* -------------------- Submit -------------------- */

  const handleSubmit = () => {
    const now = moment().format();

    const activity = [
      { ...caseNotifications[5].success, date: now, userId },
      { ...caseNotifications[5].file, date: now, userId },
      ...activeCase.activity,
    ];

    const files = [...activeCase.files];
    const newFiles = [];

    const preopFile = preop[0];

    if (preopFile) {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.designReport}_${preopFile.name}`,
        name: preopFile.name,
        type: fileTypes.designReport,
        status: fileStatuses.PENDING,
        date: now,
        userId,
      });
      newFiles.push(preopFile);
    }

    manufacturing.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.manufacturing}_${file.name}`,
        name: file.name,
        type: fileTypes.manufacturing,
        status: fileStatuses.PENDING,
        date: now,
        userId,
      });
      newFiles.push(file);
    });

    const approvedDesign = files.find(
      (f) =>
        f.type === fileTypes.designReport &&
        f.status === fileStatuses.APPROVED
    );

    updateCase(
      {
        ...activeCase,
        step: approvedDesign ? 7 : 6,
        activity,
        components: maxillaList.map((item) => ({
          product: item.product,
          amount: item.amount,
          category: item.category,
        })),
        files,
        completionDates: {
          ...activeCase.completionDates,
          step5: now,
        },
        completedBy: {
          ...activeCase.completedBy,
          step5: userId,
        },
        status: statuses.inProgress,
        notes: {
          ...activeCase.notes,
        },
        requiredDate: date.format(),
      },
      newFiles
    ).then(onClose);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="case-step-container">
        <div className="case-step-content">
          <div className="case-step-row">
            <div className="column">
              <div className="modal-item-circle">5</div>
            </div>

            <div className="modal-item-content">
              <div className="m-b-lg">Upload Design Report</div>

              {activeCase.access ? (
                <>
                  {showApprovalNote && approvalNote && (
                    <div className="m-t-lg m-b-md">
                      <div className="secondary font-size-sm m-b-sm">
                        APPROVAL NOTE
                      </div>
                      <div>{approvalNote}</div>
                    </div>
                  )}

                  <div className="m-t-lg m-b-md">
                    <div className="secondary font-size-sm m-b-sm">
                      ATTACH DESIGN REPORT (.pdf)
                    </div>

                    {designReportRejected && (
                      <div className="alert warning m-b-md">
                        <div className="d-flex align-center">
                          <WarningIcon color="primary" />
                          <div>Design report has been rejected</div>
                        </div>
                        {activeCase.notes?.step6 && (
                          <div className="m-t-md">
                            Note: {activeCase.notes.step6}
                          </div>
                        )}
                      </div>
                    )}

                    <Dropzone
                      files={preop}
                      onUpload={setPreop}
                      type="pdf"
                      icon="preop"
                      multiple={false}
                      onRemove={handleRemovePreop}
                    />
                  </div>

                  <div className="m-t-lg m-b-md">
                    <div className="secondary font-size-sm m-b-sm">
                      ATTACH MANUFACTURING FILES (.zip)
                    </div>

                    {manufacturingFilesRejected && (
                      <div className="alert warning m-b-md">
                        <div className="d-flex align-center">
                          <WarningIcon color="primary" />
                          <div>Manufacturing files have been rejected</div>
                        </div>
                        {activeCase.notes?.step7 && (
                          <div className="m-t-md">
                            Note: {activeCase.notes.step7}
                          </div>
                        )}
                      </div>
                    )}

                    <Dropzone
                      files={manufacturing}
                      onUpload={setManufacturingFiles}
                      type="zip"
                      icon="preop"
                      multiple
                      onRemove={handleRemoveManufacturing}
                    />
                  </div>

                  <div className="m-t-lg">
                    <HardwareList
                      classes={classes}
                      hardwareList={maxillaList}
                      products={products}
                      onAdd={handleAddMaxilla}
                      onRemove={handleRemoveMaxilla}
                      onChange={(id, product) =>
                        handleMaxillaChange(id, 'product', product)
                      }
                      onAmountChange={(id, v) =>
                        handleMaxillaChange(id, 'amount', v)
                      }
                      onCategoryChange={(id, v) =>
                        handleMaxillaChange(id, 'category', v)
                      }
                      title="Components"
                      procedureFlow={activeCase.procedureFlow}
                    />
                  </div>

                  <div className="hardware-list-container">
                    <div className="hardware-list-header">
                      <div className="secondary font-size-sm">
                        REQUIRED DATE
                      </div>
                    </div>

                    <DatePicker
                      desktopModeMediaQuery="(min-width: 0px)"
                      value={date}
                      onChange={(newDate) => setDate(newDate)}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          variant: 'standard',
                        },
                      }}
                    />
                  </div>
                </>
              ) : (
                <NoAccess />
              )}
            </div>
          </div>

          <CaseAccordion currentStep={5} />
        </div>

        <div className="case-modal-actions">
          <div className="secondary pointer" onClick={onClose}>
            Close
          </div>
          <Button
            text="Submit"
            color="primary"
            onClick={handleSubmit}
            disabled={
              !activeCase.access ||
              (!designReport && !preop.length) ||
              (!manufacturingFiles?.length && !manufacturing.length) ||
              !maxillaList.length
            }
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};
